import type { FuelType, Transmission } from '@/types/domain';

/**
 * Parametres de la plateforme.
 *
 * IMPORTANT — ces valeurs sont des VALEURS PAR DEFAUT, pas des constantes
 * metier. A partir de la Phase 2 elles seront stockees dans la table
 * `platform_settings` et modifiables depuis le back-office administrateur.
 * Le frontend les lit pour l'affichage ; le serveur relit toujours les siennes
 * avant de calculer un prix ou d'encaisser un paiement.
 *
 * Rien de ce qui figure ici ne doit etre duplique « en dur » ailleurs dans le code.
 */

export type CurrencyCode = 'XOF' | 'MAD' | 'EUR';

export interface PlatformSettings {
  /** Devise par defaut du marche courant. */
  currency: CurrencyCode;
  /**
   * Commission plateforme, en points de base.
   * Toujours 0 : l'application est gratuite. Le champ subsiste parce que la
   * base le renvoie encore, et parce qu'un jour ou l'autre la question se
   * reposera — mieux vaut un zero explicite qu'un concept efface.
   */
  commissionBps: number;
  /** Frais de service factures au client, en points de base du sous-total. */
  serviceFeeBps: number;
  /** Duree minimale d'une location, en jours. */
  minRentalDays: number;
  /** Duree maximale d'une location, en jours. */
  maxRentalDays: number;
  /** Delai d'annulation sans frais, en heures avant le depart. */
  freeCancellationHours: number;
  /** Age minimum du conducteur, en annees. */
  minDriverAge: number;
  /** Delai au-dela duquel une reservation non acceptee expire, en heures. */
  bookingAcceptanceTimeoutHours: number;
}

export const DEFAULT_PLATFORM_SETTINGS: PlatformSettings = {
  currency: 'XOF',
  commissionBps: 0, // application gratuite
  serviceFeeBps: 0,
  minRentalDays: 1,
  maxRentalDays: 90,
  freeCancellationHours: 48,
  minDriverAge: 21,
  bookingAcceptanceTimeoutHours: 24,
};

/* -------------------------------------------------------------------------- */
/* Referentiels (destines a passer en base en Phase 2)                         */
/* -------------------------------------------------------------------------- */

export interface VehicleCategoryDef {
  slug: string;
  label: string;
}

export const VEHICLE_CATEGORIES: readonly VehicleCategoryDef[] = [
  { slug: 'citadine', label: 'Citadine' },
  { slug: 'berline', label: 'Berline' },
  { slug: 'suv', label: 'SUV' },
  { slug: '4x4', label: '4x4' },
  { slug: 'van', label: 'Van / Minibus' },
  { slug: 'luxe', label: 'Luxe' },
  { slug: 'utilitaire', label: 'Utilitaire' },
];

export interface VehicleFeatureDef {
  slug: string;
  label: string;
  /** Affiche comme filtre sur la page resultats. */
  filterable: boolean;
}

export const VEHICLE_FEATURES: readonly VehicleFeatureDef[] = [
  { slug: 'air_conditioning', label: 'Climatisation', filterable: true },
  { slug: 'gps', label: 'GPS', filterable: true },
  { slug: 'bluetooth', label: 'Bluetooth', filterable: true },
  { slug: 'baby_seat', label: 'Siège bébé', filterable: true },
  { slug: 'usb', label: 'Prise USB', filterable: false },
  { slug: 'reverse_camera', label: 'Caméra de recul', filterable: false },
  { slug: 'roof_rack', label: 'Galerie de toit', filterable: false },
];

export interface BookingOptionDef {
  slug: string;
  label: string;
  /** Mode de facturation : forfait unique ou multiplie par le nombre de jours. */
  pricing: 'flat' | 'per_day';
}

export const BOOKING_OPTIONS: readonly BookingOptionDef[] = [
  { slug: 'delivery', label: 'Livraison du véhicule', pricing: 'flat' },
  { slug: 'pickup_return', label: 'Récupération à la restitution', pricing: 'flat' },
  { slug: 'driver', label: 'Avec chauffeur', pricing: 'per_day' },
  { slug: 'additional_driver', label: 'Conducteur supplémentaire', pricing: 'per_day' },
  { slug: 'extra_insurance', label: 'Assurance complémentaire', pricing: 'per_day' },
  { slug: 'baby_seat', label: 'Siège bébé', pricing: 'per_day' },
];

export interface CityDef {
  slug: string;
  label: string;
  country: string;
  countryCode: string;
  /** Active = proposee dans le moteur de recherche. */
  active: boolean;
}

export const CITIES: readonly CityDef[] = [
  { slug: 'abidjan', label: 'Abidjan', country: "Côte d'Ivoire", countryCode: 'CI', active: true },
  { slug: 'yamoussoukro', label: 'Yamoussoukro', country: "Côte d'Ivoire", countryCode: 'CI', active: false },
  { slug: 'bouake', label: 'Bouaké', country: "Côte d'Ivoire", countryCode: 'CI', active: false },
  { slug: 'san-pedro', label: 'San Pédro', country: "Côte d'Ivoire", countryCode: 'CI', active: false },
  { slug: 'grand-bassam', label: 'Grand-Bassam', country: "Côte d'Ivoire", countryCode: 'CI', active: false },
];

export const TRANSMISSION_LABELS: Record<Transmission, string> = {
  manual: 'Manuelle',
  automatic: 'Automatique',
};

export const FUEL_LABELS: Record<FuelType, string> = {
  petrol: 'Essence',
  diesel: 'Diesel',
  hybrid: 'Hybride',
  electric: 'Électrique',
};
